/**
 * Reconocer las paginas dobles que la fuente manda partidas por la mitad.
 *
 * Se miran **dos cosas del corte**, y hacen falta las dos:
 *
 * 1. **Que el dibujo siga.** Si dos paginas eran una sola, la tinta de un
 *    borde continua en el otro, asi que los dos bordes se parecen fila a fila.
 *    De cada borde se saca un perfil de 300 filas y se comparan con una
 *    correlacion.
 * 2. **Que no haya escalon.** Si de verdad eran una, pegarlas tiene que dar
 *    una imagen lisa: el salto de tinta entre la ultima columna de una y la
 *    primera de la otra debe parecerse al que hay entre dos columnas vecinas
 *    cualesquiera de ahi al lado. Se mide `salto / salto normal`: en una doble
 *    de verdad sale **1,0-1,5** y en dos paginas sueltas **3 o mas**.
 *
 * **Que borde con cual importa, y es lo que costo acertar.** Leyendo de derecha
 * a izquierda, de una pareja (i, i+1) la pagina i es la de la DERECHA y la i+1
 * la de la izquierda: el corte es el borde **derecho de la i+1** contra el
 * **izquierdo de la i**. Al reves —que fue el primer intento— las parejas de
 * verdad puntuan como cualquier otra cosa: la doble de "Black Meteorite" del
 * 384 sacaba +0,02 y dos paginas sueltas +0,57.
 *
 * Se mira **solo esa colocacion**, y no depende del ajuste de sentido del
 * lector: el orden de las paginas es cosa de la fuente, no de por donde
 * prefiera uno tocar para pasar. Las dobles conocidas van todas asi.
 *
 * ## Por que no basta la correlacion (2026-09-16)
 *
 * La primera version miraba solo el parecido, en una banda de 6 px, y pedia
 * 0,70. Eso se calibro contra cinco capitulos de Black Clover y daba la
 * sensacion de separar bien; leyendo **One Piece 1150** se vio que no: la doble
 * de las paginas 2-3 se queda en **0,466** y salia partida.
 *
 * No es cosa del liston. El parecido **se pierde con la distancia al corte**, y
 * lo deprisa que se pierde depende del dibujo: donde el corte cae en manchas
 * grandes (un ala, una nube) aguanta, y donde cae en textura fina (el bosque
 * de esa pagina de One Piece) se cae en tres columnas. Medido en la misma
 * doble: 0,814 con una columna, 0,708 con dos, 0,621 con tres y 0,466 con
 * seis. Bajar el liston hasta cogerla metia dos falsas que puntuaban aun mas
 * alto (Black Clover 382 paginas 2-3 con 0,664 y 383 paginas 4-5 con 0,607),
 * asi que con esta medida sola no hay numero que valga.
 *
 * Con **una sola columna** el parecido es lo mas fuerte que se puede medir,
 * pero tambien lo mas tonto: dos margenes en blanco con cuatro motas de tinta
 * correlacionan de miedo (One Piece 1000 paginas 4-5 sacaba 0,928). Ahi entra
 * el escalon, que es lo que de verdad distingue "esto era una imagen" de "esto
 * son dos": son dos medidas independientes y cada una tapa el agujero de la
 * otra.
 *
 * ## Los numeros
 *
 * Sobre 13 capitulos enteros de cuatro fuentes —One Piece 1000, 1100, 1145 y
 * 1150; Black Clover 370, 381, 382, 383, 384 y 385; Record of Ragnarok 122,
 * 123 y 124—, con cada pareja propuesta y cada una que se quedaba cerca
 * comprobada **abriendo las paginas**:
 *
 *     dobles de verdad     parecido 0,739 a 0,998   escalon 0,84 a 1,51
 *     las demas            parecido 0,000 a 0,208   escalon 2,54 a 14,0
 *
 * Los dos huecos son comodos: 0,208 contra 0,739 en el parecido y 1,51 contra
 * 2,54 en el escalon. De ahi 0,60 y 2,0.
 *
 * Contra la version anterior, en esos 13 capitulos: **gana cuatro dobles** que
 * salian partidas (One Piece 1150 paginas 2-3 y 1145 paginas 13-14, Black
 * Clover 383 paginas 10-11 y 385 paginas 4-5) y **quita tres uniones falsas**
 * que pegaba (One Piece 1145 paginas 19-20 y 1000 paginas 4-5, Record of
 * Ragnarok 122 paginas 15-16, las tres de margenes en blanco). Lo demas sale
 * igual: 21 parejas, ninguna de mas.
 *
 * Las paginas se piden con `obtenerEntrada`, que mira primero la memoria y
 * luego IndexedDB, y solo va a la red **en la cola sin prioridad**, por detras
 * de la pagina que el usuario esta esperando. En la practica no descarga nada
 * de mas: son las mismas que el lector precarga igualmente.
 *
 * Al principio esto se hacia solo con las que ya estaban en memoria, para no
 * gastar ni una peticion. Salio mal en el telefono: si la mitad siguiente
 * todavia venia de camino, la respuesta era "no se sabe" y la doble se quedaba
 * partida. Lo mismo pasaba con un capitulo ya descargado, con las paginas en
 * IndexedDB pero no en memoria.
 */

import { obtenerEntrada } from './imagenes'

/**
 * Ancho de referencia de una pagina. Lo que se mide va **en columnas de una
 * pagina de 784 px**, que es como llega del rip de MANGA Plus, y se escala si
 * la pagina viene mas grande: la misma pagina puede llegar a 784 px o a 1568
 * si esta mejorada con waifu2x, y las dos tienen que dar el mismo numero.
 *
 * Comprobado subiendo a doble las paginas de diez costuras: el parecido no se
 * mueve (0,814 contra 0,813) y el escalon tampoco (1,32 contra 1,34). Sin
 * escalar el paso, el escalon **se duplicaba** al agrandar —las columnas
 * vecinas de una imagen interpolada se parecen mas entre si, pero el corte
 * sigue siendo el mismo corte— y el liston dejaba de valer.
 */
const ANCHO_PATRON = 784
/**
 * Ancho de la franja que se lee en cada borde, en columnas patron. Es lo que
 * se usa para saber cuanto salta la tinta de una columna a la vecina **ahi
 * mismo**, que es con lo que se compara el salto del corte.
 */
const FRANJA = 6
/** Filas del perfil. Mas no afina, y cada fila es trabajo en el movil. */
const FILAS = 300
/** Parecido minimo entre los dos bordes. Ver los numeros de arriba. */
const UMBRAL = 0.6
/**
 * Cuantas veces puede el corte saltar mas que dos columnas vecinas de al lado
 * antes de dejar de ser una doble.
 */
const SALTO_MAXIMO = 2
/**
 * Mas alto que esto por su ancho ya no es una pagina de manga, es una tira:
 * una pagina normal ronda 1,45 y el umbral de manhwa de `formato.ts` es 2,5.
 */
const PROPORCION_MAXIMA = 2.2

/**
 * Como va colocada una doble: `rtl` es la pagina siguiente a la izquierda —lo
 * normal en manga, y lo unico que reconoce el automatico— y `ltr` al reves,
 * que solo sale de unir a mano.
 */
export type Orden = 'ltr' | 'rtl'

/** Lo que se mide de un borde de una pagina. */
interface Borde {
  /** Tinta media por fila de la columna del corte, resumida a `FILAS`. */
  perfil: number[]
  /** Tinta de la mismisima columna del corte, fila a fila. */
  columna: Float64Array
  /** Cuanto salta la tinta entre columnas vecinas de esa franja. */
  salto: number
}

interface Perfiles {
  izq: Borde
  der: Borde
  /** Tamano del fichero: dos mitades de una doble siempre miden lo mismo. */
  ancho: number
  alto: number
}

/** Perfiles ya medidos, por ruta de pagina. */
const medidos = new Map<string, Perfiles | null>()
/** Costuras ya calculadas, por `capituloId:i`. */
const costuras = new Map<string, number>()

/**
 * Mide un borde a partir de la franja de pixeles pegada a el.
 *
 * `alCorte` dice de que lado de la franja esta el corte: la franja del borde
 * izquierdo de una pagina empieza en el corte, y la del derecho acaba en el.
 *
 * Las cuentas se hacen **a mano**, recorriendo los pixeles. El primer intento
 * fue dibujar la banda en un lienzo de 1xFILAS y dejar que el navegador
 * promediara al reducir, que es una linea de codigo; pero `drawImage` no
 * promedia un area de 40x1145 en un pixel, sino que muestrea unas pocas
 * columnas, y los numeros salian otros: en el banco, la doble 15-16 del
 * capitulo 383 bajaba de 0,670 a 0,399 y se perdia. Recorrer los pixeles de
 * la franja cuesta unos milisegundos y da el mismo valor que la medida con la
 * que se eligieron los listones.
 */
function medirBorde(
  datos: Uint8ClampedArray,
  ancho: number,
  alto: number,
  paso: number,
  alCorte: 'izquierda' | 'derecha',
): Borde {
  const tinta = new Float64Array(ancho * alto)
  for (let y = 0; y < alto; y++) {
    for (let x = 0; x < ancho; x++) {
      const p = (y * ancho + x) * 4
      // Tinta, no luz: interesa donde hay dibujo
      tinta[y * ancho + x] = 255 - (datos[p]! * 0.299 + datos[p + 1]! * 0.587 + datos[p + 2]! * 0.114)
    }
  }

  // Las `paso` columnas pegadas al corte, que son las que mas dicen
  const desde = alCorte === 'izquierda' ? 0 : ancho - paso
  const perfil: number[] = []
  for (let f = 0; f < FILAS; f++) {
    const desdeY = Math.floor((f * alto) / FILAS)
    const hastaY = Math.max(desdeY + 1, Math.floor(((f + 1) * alto) / FILAS))
    let suma = 0
    for (let y = desdeY; y < hastaY; y++) {
      for (let x = desde; x < desde + paso; x++) suma += tinta[y * ancho + x]!
    }
    perfil.push(suma / ((hastaY - desdeY) * paso))
  }

  const columna = new Float64Array(alto)
  const x0 = alCorte === 'izquierda' ? 0 : ancho - 1
  for (let y = 0; y < alto; y++) columna[y] = tinta[y * ancho + x0]!

  // El salto normal entre columnas separadas lo mismo que las del corte
  let suma = 0
  let cuantos = 0
  for (let y = 0; y < alto; y++) {
    for (let x = 0; x + paso < ancho; x++) {
      suma += Math.abs(tinta[y * ancho + x]! - tinta[y * ancho + x + paso]!)
      cuantos++
    }
  }

  return { perfil, columna, salto: cuantos ? suma / cuantos : 0 }
}

async function perfiles(ruta: string): Promise<Perfiles | null> {
  const guardado = medidos.get(ruta)
  if (guardado !== undefined) return guardado

  // Sin prioridad: por detras de lo que el lector esta pintando
  const entrada = await obtenerEntrada(ruta, false)
  if (!entrada) return null

  try {
    const imagen = await createImageBitmap(entrada.blob)
    const ancho = imagen.width
    const alto = imagen.height
    const paso = Math.max(1, Math.round(ancho / ANCHO_PATRON))
    const franja = Math.min(
      Math.max(2 * paso, Math.round((ancho * FRANJA) / ANCHO_PATRON)),
      Math.floor(ancho / 4),
    )
    const lienzo = document.createElement('canvas')
    lienzo.width = ancho
    lienzo.height = alto
    const pincel = lienzo.getContext('2d', { willReadFrequently: true })
    if (!pincel) return null
    pincel.drawImage(imagen, 0, 0)
    imagen.close()

    const resultado: Perfiles = {
      izq: medirBorde(pincel.getImageData(0, 0, franja, alto).data, franja, alto, paso, 'izquierda'),
      der: medirBorde(
        pincel.getImageData(ancho - franja, 0, franja, alto).data,
        franja,
        alto,
        paso,
        'derecha',
      ),
      ancho,
      alto,
    }
    medidos.set(ruta, resultado)
    return resultado
  } catch {
    // Safari se niega a descodificar segun que imagenes; sin perfil no se une
    medidos.set(ruta, null)
    return null
  }
}

function correlacion(a: number[], b: number[]): number {
  const n = a.length
  let ma = 0
  let mb = 0
  for (let i = 0; i < n; i++) {
    ma += a[i]!
    mb += b[i]!
  }
  ma /= n
  mb /= n
  let arriba = 0
  let va = 0
  let vb = 0
  for (let i = 0; i < n; i++) {
    const da = a[i]! - ma
    const db = b[i]! - mb
    arriba += da * db
    va += da * da
    vb += db * db
  }
  // Un borde liso —todo margen blanco— no dice nada, y ahi la correlacion no
  // esta definida: se contesta 0 y esa costura no une nada.
  if (va === 0 || vb === 0) return 0
  return arriba / Math.sqrt(va * vb)
}

/**
 * Cuanto encaja el dibujo en el corte entre la pagina `i` y la `i+1`.
 *
 * Es el parecido de los dos bordes, o **0 si al pegarlas queda escalon**: las
 * dos medidas se resumen en un numero porque el de al lado tambien se compara
 * con este para que dos parejas no se pisen.
 *
 * Devuelve `null` si alguna de las dos no se ha podido conseguir —sin red, o
 * un 404—, que es distinto de "no encajan": se puede volver a preguntar.
 */
export async function costura(
  capituloId: number,
  paginas: string[],
  i: number,
): Promise<number | null> {
  if (i < 0 || i + 1 >= paginas.length) return null
  const clave = `${capituloId}:${i}`
  const guardada = costuras.get(clave)
  if (guardada !== undefined) return guardada

  const [a, b] = await Promise.all([perfiles(paginas[i]!), perfiles(paginas[i + 1]!)])
  if (!a || !b) return null

  /*
   * Dos mitades de una doble salen del mismo fichero partido, asi que miden
   * exactamente lo mismo. Si no coinciden no hay nada que comparar: los
   * perfiles se estiran a 300 filas y una pagina doble entera —de las que
   * llegan sin partir, 2133x1600 frente a 1200x1800— podria parecerse a una
   * normal por casualidad y acabar pegada a la de al lado.
   */
  if (a.ancho !== b.ancho || a.alto !== b.alto) {
    costuras.set(clave, 0)
    return 0
  }

  /*
   * Y una tira de manhwa no es media pagina de nada. Solo se mira si el
   * lector esta en paginado, pero el usuario puede forzarlo en una serie que
   * sea tira —o la deteccion de formato puede fallar—, y ahi dos tiras
   * seguidas no tienen por que parecerse menos que dos paginas.
   */
  if (a.alto > a.ancho * PROPORCION_MAXIMA) {
    costuras.set(clave, 0)
    return 0
  }

  // La siguiente va a la izquierda, asi que el corte es su borde derecho
  // contra el izquierdo de esta
  const parecido = correlacion(b.der.perfil, a.izq.perfil)

  // Y el escalon del corte, contra el que hay entre columnas vecinas de al lado
  let salto = 0
  for (let y = 0; y < a.alto; y++) salto += Math.abs(b.der.columna[y]! - a.izq.columna[y]!)
  salto /= a.alto
  const normal = (a.izq.salto + b.der.salto) / 2
  // Dos margenes en blanco no tienen ni salto ni columnas vecinas que comparar;
  // ahi manda el parecido, que en un borde liso ya vale 0.
  const escalon = normal > 0 ? salto / normal : 0

  const valor = escalon > SALTO_MAXIMO ? 0 : parecido
  costuras.set(clave, valor)
  return valor
}

/**
 * ¿Son la pagina `i` y la `i+1` las dos mitades de una doble?
 *
 * `null` es "todavia no se sabe": alguna pagina no se ha podido conseguir y
 * hay que volver a preguntar mas tarde.
 *
 * Dos parejas no pueden pisarse, asi que una costura solo vale si es mejor que
 * las de al lado. Sin esa regla, en el capitulo 384 la doble de las paginas 19
 * y 20 arrastraba tambien a la 20 con la 21.
 */
export async function esPareja(
  capituloId: number,
  paginas: string[],
  i: number,
): Promise<boolean | null> {
  const suya = await costura(capituloId, paginas, i)
  if (suya === null) return null
  if (suya < UMBRAL) return false
  const [antes, despues] = await Promise.all([
    costura(capituloId, paginas, i - 1),
    costura(capituloId, paginas, i + 1),
  ])
  if (antes !== null && antes >= suya) return false
  if (despues !== null && despues >= suya) return false
  return true
}

/** Se olvida lo medido de un capitulo. Lo usa el rescate de truncados. */
export function olvidarCosturas(capituloId: number, paginas: string[]): void {
  for (const ruta of paginas) medidos.delete(ruta)
  for (const clave of [...costuras.keys()]) {
    if (clave.startsWith(`${capituloId}:`)) costuras.delete(clave)
  }
}
